import { useState } from 'react';
import { ENDPOINT_V2 } from '../api';
import { IconArrowRight } from './Icon';

/** Public contact form — posts to /contact/send (rate-limited, Telegram-bridged). */
export default function ContactForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [sending, setSending] = useState(false);
  const [ok, setOk] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null); setOk(null);
    if (!name.trim() || !email.trim() || !message.trim()) {
      setErr('Please fill in name, email and message.'); return;
    }
    if (message.trim().length < 10) { setErr('Message is too short (min 10 chars).'); return; }
    setSending(true);
    try {
      const r = await fetch(`${ENDPOINT_V2}/contact/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), subject: subject.trim(), message: message.trim(), website }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.message || `Error ${r.status}`);
      setOk("Message sent — we'll reply via email or Telegram within hours.");
      setName(''); setEmail(''); setSubject(''); setMessage('');
    } catch (ex: any) {
      setErr(ex.message || 'Network error. Try emailing hello@tnidu.com directly.');
    } finally { setSending(false); }
  }

  return (
    <form onSubmit={submit} className="glass p-6 sm:p-8">
      <h3 className="font-[family-name:var(--font-display)] font-bold text-xl mb-1">Send us a message</h3>
      <p className="text-sm text-[color:var(--color-ink-dim)] mb-6">
        Goes straight to our Telegram — a human replies within hours. Prefer email? <a className="text-[color:var(--color-brand-glow)] underline" href="mailto:hello@tnidu.com">hello@tnidu.com</a>
      </p>
      {ok && <div className="mb-4 px-4 py-3 rounded-xl bg-[color:var(--color-ok)]/15 border border-[color:var(--color-ok)]/30 text-sm text-[color:var(--color-ok)]">{ok}</div>}
      {err && <div className="mb-4 px-4 py-3 rounded-xl bg-[color:var(--color-danger)]/15 border border-[color:var(--color-danger)]/30 text-sm text-[color:var(--color-danger)]">{err}</div>}
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)]">Name *</span>
          <input value={name} onChange={e => setName(e.target.value)} required maxLength={80} placeholder="Your name" className="mt-1 w-full rounded-xl bg-white/[0.04] border border-[color:var(--color-line)] px-4 py-3 text-sm focus:outline-none focus:border-[color:var(--color-brand)]/60" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)]">Email *</span>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} required maxLength={120} placeholder="you@example.com" className="mt-1 w-full rounded-xl bg-white/[0.04] border border-[color:var(--color-line)] px-4 py-3 text-sm focus:outline-none focus:border-[color:var(--color-brand)]/60" />
        </label>
      </div>
      <label className="block mt-4">
        <span className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)]">Subject</span>
        <input value={subject} onChange={e => setSubject(e.target.value)} maxLength={120} placeholder="Billing, feature request, bug report…" className="mt-1 w-full rounded-xl bg-white/[0.04] border border-[color:var(--color-line)] px-4 py-3 text-sm focus:outline-none focus:border-[color:var(--color-brand)]/60" />
      </label>
      <label className="block mt-4">
        <span className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)]">Message *</span>
        <textarea value={message} onChange={e => setMessage(e.target.value)} required maxLength={4000} rows={5} placeholder="How can we help?" className="mt-1 w-full rounded-xl bg-white/[0.04] border border-[color:var(--color-line)] px-4 py-3 text-sm focus:outline-none focus:border-[color:var(--color-brand)]/60 resize-y" />
        <div className="text-xs text-[color:var(--color-ink-faint)] mt-1">{message.length}/4000</div>
      </label>
      {/* honeypot */}
      <input type="text" value={website} onChange={e => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden />
      <button type="submit" disabled={sending} className="btn btn-primary mt-6 w-full sm:w-auto disabled:opacity-60 inline-flex items-center gap-2">
        {sending ? 'Sending…' : <>Send message via Telegram <IconArrowRight width={14} height={14} /></>}
      </button>
      <p className="text-xs text-[color:var(--color-ink-faint)] mt-3">We reply by email. No spam, no newsletter. Response time: usually &lt;3h on weekdays.</p>
    </form>
  );
}
